import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Menu, SlidersHorizontal, Tags, UserRoundPlus } from 'lucide-react';
import { useHasPermission } from '@shared/lib/permissions';
import { env } from '@config/env';
import { useHomeLayoutStore, resolveWidgets } from '@shared/stores/homeLayoutStore';
import { WIDGET_REGISTRY } from '@features/dashboard/widgets/registry';
import { CustomizeHomeSheet } from '@features/dashboard/CustomizeHomeSheet';
import { tileColSpanClass } from '@shared/lib/tileGrid';
import { NotificationBell } from '@features/notifications/NotificationBell';
import { MoreCategoryTilesSheet } from '@features/dashboard/MoreCategoryTilesSheet';
import { PropertySwitcher } from '@features/dashboard/PropertySwitcher';
import { useAuthStore } from '@shared/stores/authStore';
import { useExperienceStore } from '@shared/stores/experienceStore';
import { useModuleStore } from '@shared/stores/moduleStore';
import { usePropertyStore } from '@shared/stores/propertyStore';
import { useUiStore } from '@shared/stores/uiStore';
import { useBrandImagery } from '@shared/hooks/useBrandImagery';
import { fetchMyProperties } from '@features/properties/api';
import { runTileAction, isPanicTile } from '@shared/lib/tileActions';
import { homeTileGridClass } from '@shared/lib/tileGrid';
import { pageCopy } from '@shared/lib/pageCopy';
import { useMemberProfile } from '@shared/hooks/useMemberProfile';
import { memberGreetingName } from '@features/profile/api';
import { QuickTile } from '@features/dashboard/QuickTile';
import { TileIcon, TileLabel, homeTileButtonClass } from '@features/dashboard/TileIcon';
import { PanicSosBadge } from '@features/panic/PanicSosBadge';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { BannerCarousel } from '@features/dashboard/BannerCarousel';
import { useHomeBannerLocations } from '@shared/hooks/useBannerLocation';
import type { CompiledMoreTileCategory } from '@shared/types/domainConfig';

/**
 * Home / dashboard — Fancourt layout: a domain-resolved photo header
 * carrying the greeting and property switcher, over a tile grid driven
 * by the tenant's domain config (`experienceStore.tiles.main`).
 */
export function Home(): JSX.Element {
  const navigate = useNavigate();
  const canMemberRequests = useHasPermission('members:requests:approve');
  const user = useAuthStore((s) => s.user);
  const profileQuery = useMemberProfile();
  const experience = useExperienceStore((s) => s.experience);
  const moreCategories = useExperienceStore((s) => s.getMoreCategories());
  const { heroImage } = useBrandImagery();
  const openNavDrawer = useUiStore((s) => s.openNavDrawer);
  const [categorySheet, setCategorySheet] = useState<CompiledMoreTileCategory | null>(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const { topBanner, bottomBanner } = useHomeBannerLocations();

  // Widget mode: a horizontal Quick Links rail + configurable widgets below.
  const widgetMode = !!env.home?.widgetMode;
  const widgetOrder = useHomeLayoutStore((s) => s.order);
  const widgetEnabledOverride = useHomeLayoutStore((s) => s.enabledOverride);
  const activeWidgets = useMemo(
    () =>
      resolveWidgets(env.home?.widgets, {
        order: widgetOrder,
        enabledOverride: widgetEnabledOverride,
      }).filter((w) => w.enabled && WIDGET_REGISTRY[w.type]),
    [widgetOrder, widgetEnabledOverride],
  );
  const propertiesModuleEnabled = useModuleStore((s) => s.isModuleEnabled('properties'));
  const activePropertyId = usePropertyStore((s) => s.activePropertyId);

  const propertiesQuery = useQuery({
    queryKey: ['my-properties'],
    queryFn: fetchMyProperties,
    enabled: propertiesModuleEnabled,
    staleTime: 60_000,
  });
  const activeProperty =
    propertiesQuery.data?.find((p) => p.property_id === activePropertyId) ??
    propertiesQuery.data?.[0];
  const homeCopy = pageCopy().home;
  const estateLine =
    propertiesModuleEnabled && activeProperty?.estate_name
      ? activeProperty.estate_name
      : homeCopy.defaultEstateLine;
  const dateLine = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const greetingName = memberGreetingName(profileQuery.data, user);
  const profileInitial = greetingName.charAt(0).toUpperCase();

  // The backend already compiles `tiles.main` per tenant — render it as-is,
  // sorted by order. (Re-gating client-side wrongly hid every tile because
  // `moduleConfig` only carries settings blocks, not a full enablement map.)
  const tiles = useMemo(
    () => [...(experience?.tiles?.main ?? [])].sort((a, b) => a.order - b.order),
    [experience],
  );

  // One home-grid tile per `more_categories` entry (member portal parity).
  const categoryTiles = useMemo(
    () =>
      moreCategories.filter((category) => (category.tiles?.length ?? 0) > 0),
    [moreCategories],
  );

  return (
    <div className="min-h-full bg-white">
      {/*
        Sticky on the OUTER shell (not the header itself): a sticky child can't
        escape its parent's box, and this wrapper is exactly header-height, so
        sticking the wrapper is what actually pins the header while content
        scrolls under. The wrapper carries the rounded shadow; the header inside
        stays `relative` (sticky={false}) so its bg/overlay clip to the rounded
        corners. z-30 keeps it under modals/sheets/tab-bar (z-50+).
      */}
      <div className="sticky top-0 z-30 overflow-visible rounded-b-3xl shadow-[0_14px_32px_-8px_rgba(0,0,0,0.28)]">
        {/* ── Photo header ─────────────────────────────────────────── */}
        <HeroHeader
          backgroundImage={heroImage}
          sticky={false}
          showBack={false}
          bodyClassName="mt-5"
          eyebrowClassName="mt-0"
          eyebrow={greetingFor(new Date())}
          title={greetingName}
          subtitle={
            <>
              {estateLine} · {dateLine}
            </>
          }
          subtitleClassName="mt-1 max-w-none text-white/65"
          leading={
            <div className="flex min-w-0 items-center gap-2">
              <button
                type="button"
                onClick={openNavDrawer}
                aria-label="Open menu"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-white/15 text-white backdrop-blur"
              >
                <Menu className="h-4.5 w-4.5" strokeWidth={1.75} />
              </button>
              {propertiesModuleEnabled ? <PropertySwitcher /> : null}
            </div>
          }
          action={
            <div className="flex shrink-0 items-center gap-2">
              <NotificationBell variant="header" />
              <button
                type="button"
                aria-label="Profile"
                onClick={() => navigate('/app/profile')}
                className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-pill bg-brass text-brass-ink"
              >
                {profileQuery.data?.profile_image_url && !avatarFailed ? (
                  <img
                    src={profileQuery.data.profile_image_url}
                    alt=""
                    className="h-full w-full object-cover"
                    onError={() => setAvatarFailed(true)}
                  />
                ) : (
                  <span className="text-body-sm font-semibold">{profileInitial}</span>
                )}
              </button>
            </div>
          }
        />
      </div>

      {topBanner ? (
        <section className="bg-white px-5 pt-4">
          <BannerCarousel
            config={topBanner}
            className="aspect-[16/7] rounded-xl shadow-[0_12px_24px_-20px_rgba(17,24,39,0.45)]"
          />
        </section>
      ) : null}

      {/* ── Tiles ────────────────────────────────────────────────── */}
      {widgetMode ? (
        <section className="bg-white px-5 pt-6 pb-safe-or-6">
          {/* Quick Links — horizontal scrollable rail */}
          <h2 className="mb-3 text-body font-bold text-ink">Quick Links</h2>

          {tiles.length > 0 || categoryTiles.length > 0 || canMemberRequests ? (
            <div className="-mx-5 -my-4 flex gap-2 overflow-x-auto px-5 py-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {tiles.map((tile) => (
                <RailTile
                  key={tile.id}
                  label={tile.label}
                  imageUrl={tile.image_url}
                  isPanic={isPanicTile(tile)}
                  onSelect={() => runTileAction(tile, navigate)}
                />
              ))}
              {categoryTiles.map((category) => (
                <RailTile
                  key={category.category_id}
                  label={category.name}
                  fallbackIcon={Tags}
                  onSelect={() => setCategorySheet(category)}
                />
              ))}
              {/* Admin: pending member join requests (members:requests:approve). */}
              {canMemberRequests ? (
                <RailTile
                  label="Member requests"
                  fallbackIcon={UserRoundPlus}
                  onSelect={() => navigate('/app/member-requests')}
                />
              ) : null}
            </div>
          ) : null}

          {/* Widgets */}
          {activeWidgets.length > 0 ? (
            <div className="mt-5 flex flex-col gap-4">
              {activeWidgets.map((widget) => {
                const def = WIDGET_REGISTRY[widget.type];
                if (!def) return null;
                const Widget = def.component;
                return <Widget key={widget.id} config={widget} />;
              })}
            </div>
          ) : null}

          {/* Customize home — manage which widgets show + their order. */}
          <button
            type="button"
            onClick={() => setCustomizeOpen(true)}
            className="mt-5 flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary-50 py-3 text-body-sm font-semibold text-primary-700"
          >
            <SlidersHorizontal className="h-4 w-4" strokeWidth={2} />
            Customize home
          </button>
        </section>
      ) : (
        <section className="bg-white px-5 pt-6 pb-safe-or-6">
          {tiles.length > 0 || categoryTiles.length > 0 ? (
            <ul className={homeTileGridClass()}>
              {tiles.map((tile) => (
                <QuickTile key={tile.id} tile={tile} onSelect={() => runTileAction(tile, navigate)} />
              ))}
              {categoryTiles.map((category) => (
                <MoreCategoryTile
                  key={category.category_id}
                  label={category.name}
                  onSelect={() => setCategorySheet(category)}
                />
              ))}
            </ul>
          ) : (
            <p className="rounded-lg bg-sunken px-4 py-8 text-center text-body-sm text-ink-secondary">
              Your community hasn't published any quick links yet.
            </p>
          )}
        </section>
      )}

      {bottomBanner ? (
        <section className="bg-white px-5 pb-safe-or-6">
          <BannerCarousel
            config={bottomBanner}
            className="aspect-[16/7] rounded-xl shadow-[0_12px_24px_-20px_rgba(17,24,39,0.45)]"
          />
        </section>
      ) : null}

      <CustomizeHomeSheet open={customizeOpen} onOpenChange={setCustomizeOpen} />

      <MoreCategoryTilesSheet
        category={categorySheet}
        open={categorySheet !== null}
        onOpenChange={(open) => {
          if (!open) setCategorySheet(null);
        }}
      />
    </div>
  );
}

function RailTile({
  label,
  imageUrl,
  fallbackIcon,
  isPanic,
  onSelect,
}: {
  label: string;
  imageUrl?: string;
  fallbackIcon?: typeof Tags;
  isPanic?: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <div className="w-[5rem] shrink-0">
      <button type="button" onClick={onSelect} className={homeTileButtonClass}>
        {isPanic ? (
          <span className="flex h-full w-full items-center justify-center">
            <PanicSosBadge className="h-12 w-12 text-sm" />
          </span>
        ) : (
          <>
            <TileIcon imageUrl={imageUrl} label={label} fallbackIcon={fallbackIcon} />
            <TileLabel label={label} />
          </>
        )}
      </button>
    </div>
  );
}

function MoreCategoryTile({
  label,
  onSelect,
}: {
  label: string;
  onSelect: () => void;
}): JSX.Element {
  return (
    <li className={tileColSpanClass(1)}>
      <button type="button" onClick={onSelect} className={homeTileButtonClass}>
        <TileIcon label={label} fallbackIcon={Tags} />
        <TileLabel label={label} />
      </button>
    </li>
  );
}

function greetingFor(date: Date): string {
  const hour = date.getHours();
  if (hour < 12 && hour >= 5) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}
